import { Injectable, inject } from '@angular/core';
import { HttpService, UrlService } from '@boels/helpers';
import {
  CategoryList,
  ContactFormDetails,
  CustomerTypeList,
  RegardingList,
} from '@boels/models';
import {
  EMPTY,
  Observable,
  combineLatest,
  forkJoin,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { BoelsReCaptchaV3Service } from '../../recaptcha/boels-recaptcha.service';
import { SiteContextFacade } from '../../site-context/facade/site-context.facade';
import { UserProfileFacade } from '../../user-profile/facade/user-profile.facade';

@Injectable({
  providedIn: 'any',
})
export class CustomerContactGateway {
  private urlService = inject(UrlService);
  private siteContextFacade = inject(SiteContextFacade);
  private cultureFacade = inject(CultureFacade);
  private userProfileFacade = inject(UserProfileFacade);
  private readonly http = inject(HttpService);
  private recaptchaService = inject(BoelsReCaptchaV3Service, {
    optional: true,
  });

  submitForm(formData: any): Observable<any> {
    if (!this.recaptchaService) return EMPTY;
    return this.recaptchaService.execute('contactForm').pipe(
      withLatestFrom(
        this.siteContextFacade.getActiveBaseSiteEntity(),
        this.cultureFacade.getActiveLanguage(),
        this.cultureFacade.getActiveCurrency(),
      ),
      map(([recaptchaToken, baseSiteId, lang, currency]) => ({
        params: [
          ['recaptchaToken', recaptchaToken],
          ['lang', lang],
          ['curr', currency],
        ],
        url: this.urlService.combineUri(
          baseSiteId.uid,
          '/customer-service/contact',
        ),
      })),
      switchMap(({ url, params }) => this.http.post(url, formData, params)),
    );
  }

  loadDetails(): Observable<ContactFormDetails> {
    return combineLatest([
      this.siteContextFacade.getActiveBaseSiteEntity(),
      this.cultureFacade.getActiveLanguage(),
      this.cultureFacade.getActiveCurrency(),
    ]).pipe(
      map(([activeBaseSite, lang, currency]) => {
        const params = [
          ['lang', lang],
          ['curr', currency],
        ];

        return { uid: activeBaseSite.uid, params };
      }),
      switchMap(({ uid, params }) =>
        forkJoin({
          dataRegarding: this.getRegarding(uid, params),
          dataCategory: this.getDataCategory(uid, params),
          dataCustomerType: this.getCustomerType(uid, params),
          titleList: this.userProfileFacade.getTitles().pipe(
            take(1),
            map((data) => data.titles),
          ),
          countryList: this.userProfileFacade.getCountries().pipe(
            take(1),
            map(
              (data) => data.countries as { isocode: string; name: string }[],
            ),
          ),
        }),
      ),
      map(
        ({
          dataRegarding,
          dataCategory,
          dataCustomerType,
          titleList,
          countryList,
        }) => {
          return {
            reList: dataRegarding.reList,
            categoryList: dataCategory.categoryList,
            customerTypeList: dataCustomerType.customerTypeList,
            titleList,
            countryList,
          };
        },
      ),
    );
  }

  private getRegarding(
    uid: string | undefined,
    params: string[][],
  ): Observable<RegardingList> {
    return this.http.get<RegardingList>(
      this.urlService.combineUri(uid, 'customer-service/contact/regarding'),
      params,
    );
  }
  private getCustomerType(
    uid: string | undefined,
    params: string[][],
  ): Observable<CustomerTypeList> {
    return this.http.get<CustomerTypeList>(
      this.urlService.combineUri(uid, 'customer-service/contact/customer-type'),
      params,
    );
  }
  private getDataCategory(
    uid: string | undefined,
    params: string[][],
  ): Observable<CategoryList> {
    return this.http.get<CategoryList>(
      this.urlService.combineUri(uid, 'customer-service/contact/category'),
      params,
    );
  }
}
